
import React from "react";
import { Button } from "@/components/ui/button";
import VisualFeatureBlock from "./VisualFeatureBlock";
import { useLanguage } from "@/contexts/LanguageContext";

const NewStory = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Des moments intemporels",
      story1: {
        title: "Le jour où Léa a reçu un cadeau venu du passé",
        description: "À son 18ème anniversaire, Léa a reçu une notification. C'était une capsule Legatia créée pour elle 12 ans plus tôt. En l'ouvrant, elle a découvert un message vidéo de sa grand-mère, enregistré alors qu'Léa n'avait que 6 ans. Sa grand-mère parlait directement à la jeune adulte qu'elle était devenue, partageant sa fierté et ses espoirs. Avec le message, une contribution financière avait grandi pour l'aider dans ses études. Léa était bouleversée : 'C'était comme si grand-mère pouvait voir qui je suis devenue. Comme si elle avait toujours été là, à chaque étape.'",
        imageAlt: "Moment de découverte d'une capsule temporelle"
      },
      story2: {
        title: "L'origine de Legatia : une boîte, un souvenir, une révélation",
        description: "Thomas, le fondateur de Legatia, a reçu pour ses 16 ans une boîte préparée par son grand-père décédé deux ans plus tôt. À l'intérieur, des lettres écrites sur 15 ans, des photos, des conseils et un petit investissement qui avait fructifié. 'Je me souviens d'avoir senti sa présence comme s'il était là, à me parler à travers les années. C'était plus qu'un souvenir, c'était une continuité. Ce jour-là, j'ai compris qu'on pouvait créer des ponts émotionnels à travers le temps. C'est ce qui a inspiré Legatia : permettre à chacun de transcender les contraintes du temps pour être là, vraiment là, aux moments qui comptent.'",
        imageAlt: "Fondateur de Legatia avec une boîte à souvenirs"
      },
      buttonText: "Créer votre capsule temporelle émotionnelle"
    },
    en: {
      title: "Timeless Moments",
      story1: {
        title: "The day Léa received a gift from the past",
        description: "On her 18th birthday, Léa received a notification. It was a Legatia capsule created for her 12 years earlier. Upon opening it, she discovered a video message from her grandmother, recorded when Léa was only 6 years old. Her grandmother spoke directly to the young adult she had become, sharing her pride and hopes. Along with the message, a financial contribution had grown to help with her studies. Léa was overwhelmed: 'It was as if grandmother could see who I've become. As if she had always been there, at every step.'",
        imageAlt: "Moment of discovery of a time capsule"
      },
      story2: {
        title: "The origin of Legatia: a box, a memory, a revelation",
        description: "Thomas, the founder of Legatia, received on his 16th birthday a box prepared by his grandfather who had passed away two years earlier. Inside were letters written over 15 years, photos, advice, and a small investment that had grown. 'I remember feeling his presence as if he was there, talking to me across the years. It was more than a memory, it was a continuity. That day, I understood that we could create emotional bridges across time. That's what inspired Legatia: allowing everyone to transcend the constraints of time to be there, truly there, at the moments that matter.'",
        imageAlt: "Founder of Legatia with a memory box"
      },
      buttonText: "Create your emotional time capsule"
    },
    de: {
      title: "Zeitlose Momente",
      story1: {
        title: "Der Tag, an dem Léa ein Geschenk aus der Vergangenheit erhielt",
        description: "An ihrem 18. Geburtstag erhielt Léa eine Benachrichtigung. Es war eine Legatia-Kapsel, die 12 Jahre zuvor für sie erstellt wurde. Beim Öffnen entdeckte sie eine Videonachricht ihrer Großmutter, aufgenommen als Léa erst 6 Jahre alt war. Ihre Großmutter sprach direkt zu der jungen Erwachsenen, die sie geworden war, und teilte ihren Stolz und ihre Hoffnungen mit. Mit der Nachricht war ein finanzieller Beitrag gewachsen, um ihr beim Studium zu helfen. Léa war überwältigt: 'Es war, als könnte Großmutter sehen, wer ich geworden bin. Als wäre sie immer da gewesen, bei jedem Schritt.'",
        imageAlt: "Moment der Entdeckung einer Zeitkapsel"
      },
      story2: {
        title: "Der Ursprung von Legatia: eine Box, eine Erinnerung, eine Offenbarung",
        description: "Thomas, der Gründer von Legatia, erhielt an seinem 16. Geburtstag eine Box, die von seinem zwei Jahre zuvor verstorbenen Großvater vorbereitet wurde. Darin befanden sich Briefe, die über 15 Jahre geschrieben wurden, Fotos, Ratschläge und eine kleine Investition, die gewachsen war. 'Ich erinnere mich, dass ich seine Präsenz spürte, als wäre er da und würde über die Jahre hinweg mit mir sprechen. Es war mehr als eine Erinnerung, es war eine Kontinuität. An diesem Tag verstand ich, dass wir emotionale Brücken über die Zeit hinweg bauen können. Das hat Legatia inspiriert: jedem zu ermöglichen, die Einschränkungen der Zeit zu überwinden, um wirklich da zu sein, in den Momenten, die zählen.'",
        imageAlt: "Gründer von Legatia mit einer Erinnerungsbox"
      },
      buttonText: "Erstellen Sie Ihre emotionale Zeitkapsel"
    }
  };

  return (
    <section id="story" className="section-padding bg-gradient-to-br from-legatia-green/10 to-legatia-blue/5">
      <div className="container mx-auto">
        <div className="max-w-5xl mx-auto">
          <h2 className="section-title text-center mb-16">
            {language === 'fr' ? (
              <>Des <span className="gradient-text">moments intemporels</span></>
            ) : language === 'en' ? (
              <><span className="gradient-text">Timeless</span> Moments</>
            ) : (
              <><span className="gradient-text">Zeitlose</span> Momente</>
            )}
          </h2>
          
          <VisualFeatureBlock 
            title={content[language].story1.title}
            description={content[language].story1.description}
            imageUrl="/assets/discovery-moment.jpg"
            imageAlt={content[language].story1.imageAlt}
            className="mb-16"
          />
          
          <VisualFeatureBlock 
            title={content[language].story2.title}
            description={content[language].story2.description}
            imageUrl="/assets/founder-story.jpg"
            imageAlt={content[language].story2.imageAlt}
            reverse={true}
            className="mb-16"
          />
          
          <div className="text-center">
            <Button className="btn-primary">{content[language].buttonText}</Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewStory;
